/**
 * BottomTabBar - Twitter-style mobile bottom navigation.
 * Visible only under md breakpoint. Tabs: Home, Search, Notifications, Store.
 * Messages stays in the header on mobile. Includes safe-area padding for iOS home indicator.
 */

import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useNotificationCount } from '../../utils/NotificationCountContext';
import NavIcon from './NavIcon';

const TABS = [
  { to: '/', label: 'Home', icon: 'home', end: true },
  { to: '/search', label: 'Search', icon: 'search' },
  { to: '/notifications', label: 'Notifications', icon: 'notifications' },
  { to: '/store', label: 'Store', icon: 'store' },
];

function BottomTabBar() {
  const { unreadCount } = useNotificationCount();
  const [keyboardTargetFocused, setKeyboardTargetFocused] = useState(false);

  useEffect(() => {
    const isKeyboardTarget = (target) =>
      target instanceof HTMLElement &&
      (target.matches('input, textarea, select') || target.isContentEditable);

    const handleFocusIn = (e) => setKeyboardTargetFocused(isKeyboardTarget(e.target));
    const handleFocusOut = () => {
      window.setTimeout(() => {
        setKeyboardTargetFocused(isKeyboardTarget(document.activeElement));
      }, 0);
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  if (keyboardTargetFocused) return null;

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-20 flex justify-around items-stretch bg-charcoal/95 backdrop-blur border-t border-shadow pb-safe-bottom"
      data-testid="bottom-tab-bar"
      aria-label="Primary"
    >
      {TABS.map((tab) => {
        const showBadge = tab.to === '/notifications' && unreadCount > 0;
        return (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          aria-label={tab.label}
          className={({ isActive }) =>
            `relative flex-1 flex items-center justify-center py-3 transition-colors ${
              isActive ? 'text-ether' : 'text-ash hover:text-ether'
            }`
          }
        >
          <span className="relative inline-flex">
            <NavIcon icon={tab.icon} className="w-6 h-6" />
            {showBadge && (
              <span
                className="absolute -top-0.5 -right-1.5 min-w-[1rem] h-4 px-0.5 rounded-full bg-ether text-void text-[9px] font-bold flex items-center justify-center leading-none"
                aria-hidden
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </span>
        </NavLink>
        );
      })}
    </nav>
  );
}

export default BottomTabBar;
